import type {
  WorkflowFileChildren,
  WorkflowFileItem,
  WorkflowFileMetadata,
  WorkflowFileRemoveResult,
  WorkflowFileUploadProgress,
} from '@cims-online/shared'
import { rest } from 'utils/axiosInstance'
import axios from 'axios'

/** How many throttled requests are retried before the refusal is surfaced. */
const MAX_THROTTLE_RETRIES = 2

/** The longest `Retry-After` delay the browser waits out automatically. */
const MAX_THROTTLE_DELAY_MS = 5000

/**
 * How long status and cancellation wait for another request on the same upload
 * session to finish. Covers a server request whose browser response was lost.
 */
const MAX_BUSY_SESSION_WAIT_MS = 120_000

/** The delay between attempts while an upload session is busy. */
const BUSY_SESSION_RETRY_DELAY_MS = 1000

/** Waits `delay` milliseconds, rejecting early when `signal` aborts. */
function wait(delay: number, signal?: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new Error('Cancelled'))
      return
    }
    const cancel = () => {
      clearTimeout(timer)
      signal?.removeEventListener('abort', cancel)
      reject(new Error('Cancelled'))
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', cancel)
      resolve()
    }, delay)
    signal?.addEventListener('abort', cancel, { once: true })
  })
}

/**
 * Sends a file request and returns its response data.
 *
 * Retries only an explicit throttle refusal with a short `Retry-After` delay,
 * and stops waiting when `signal` aborts.
 */
async function requestWorkflowFile<T>(
  send: () => Promise<{ data: T }>,
  signal?: AbortSignal
): Promise<T> {
  for (let attempt = 0; ; attempt++) {
    try {
      return (await send()).data
    } catch (error) {
      if (!axios.isAxiosError(error) || attempt >= MAX_THROTTLE_RETRIES)
        throw error
      const delay = Number(error.response?.headers?.['retry-after']) * 1000
      if (
        !Number.isFinite(delay) ||
        delay <= 0 ||
        delay > MAX_THROTTLE_DELAY_MS
      )
        throw error
      await wait(delay, signal)
    }
  }
}

/**
 * Whether a failure reports that another request holds the upload session,
 * such as one whose browser response was lost while the server kept working.
 */
export function isWorkflowFileSessionBusy(error: unknown) {
  if (!axios.isAxiosError(error) || error.response?.status !== 409) return false
  const data: unknown = error.response.data
  return (
    !!data &&
    typeof data === 'object' &&
    'reason' in data &&
    data.reason === 'busy'
  )
}

/**
 * Sends a session request, repeating it while another request holds the
 * session, for at most {@link MAX_BUSY_SESSION_WAIT_MS}.
 *
 * Status and cancellation are safe to repeat, and their result is only
 * trustworthy once no earlier request on the session can still write.
 */
async function requestIdleWorkflowFileSession<T>(
  send: () => Promise<{ data: T }>,
  signal?: AbortSignal
): Promise<T> {
  const deadline = Date.now() + MAX_BUSY_SESSION_WAIT_MS
  for (;;) {
    try {
      return await requestWorkflowFile(send, signal)
    } catch (error) {
      if (
        !isWorkflowFileSessionBusy(error) ||
        Date.now() + BUSY_SESSION_RETRY_DELAY_MS > deadline
      )
        throw error
      await wait(BUSY_SESSION_RETRY_DELAY_MS, signal)
    }
  }
}

/**
 * Creates REST adapters for one record's SharePoint files, shared by the files
 * drawer and the reusable file manager.
 *
 * `cancel` deliberately takes no signal so cleanup can outlive an unmounted
 * manager. `status` and `cancel` wait out a busy session; see
 * {@link requestIdleWorkflowFileSession}.
 * Chunk progress reports bytes sent to the API, before SharePoint acceptance.
 */
export function createWorkflowFileOperations(
  projectId: string,
  recordId: string
) {
  const base = `/api/projects/${encodeURIComponent(
    projectId
  )}/sharepoint/workflow-record-files/${encodeURIComponent(recordId)}`
  const json = { 'Content-Type': 'application/json' }
  return {
    metadata: (signal?: AbortSignal) =>
      requestWorkflowFile(
        () => rest.get<WorkflowFileMetadata>(base, { signal }),
        signal
      ),
    children: (folderId: string, signal?: AbortSignal) =>
      requestWorkflowFile(
        () =>
          rest.get<WorkflowFileChildren>(base + '/children', {
            params: { folderId },
            signal,
          }),
        signal
      ),
    folder: (parentId: string, name: string, signal?: AbortSignal) =>
      requestWorkflowFile(
        () =>
          rest.post<
            { parentId: string; name: string },
            { data: WorkflowFileItem }
          >(base + '/folders', { parentId, name }, { headers: json, signal }),
        signal
      ),
    initialize: (parentId: string, file: File, signal?: AbortSignal) =>
      requestWorkflowFile(
        () =>
          rest.post<
            { parentId: string; name: string; size: number },
            { data: WorkflowFileUploadProgress }
          >(
            base + '/uploads',
            { parentId, name: file.name, size: file.size },
            { headers: json, signal }
          ),
        signal
      ),
    chunk: (
      sessionId: string,
      offset: number,
      bytes: Blob,
      signal?: AbortSignal,
      onBytesSent?: (loaded: number) => void
    ) =>
      requestWorkflowFile(
        () =>
          rest.put<Blob, { data: WorkflowFileUploadProgress }>(
            `${base}/uploads/${encodeURIComponent(sessionId)}/content`,
            bytes,
            {
              params: { offset },
              headers: { 'Content-Type': 'application/octet-stream' },
              signal,
              onUploadProgress: onBytesSent
                ? (event: ProgressEvent) =>
                    onBytesSent(Math.min(event.loaded, bytes.size))
                : undefined,
            }
          ),
        signal
      ),
    status: (sessionId: string, signal?: AbortSignal) =>
      requestIdleWorkflowFileSession(
        () =>
          rest.get<WorkflowFileUploadProgress>(
            `${base}/uploads/${encodeURIComponent(sessionId)}`,
            { signal }
          ),
        signal
      ),
    complete: (sessionId: string, signal?: AbortSignal) =>
      requestWorkflowFile(
        () =>
          // The browser adapter drops Content-Type without a body, and the router requires JSON.
          rest.post<
            Record<string, never>,
            { data: WorkflowFileUploadProgress }
          >(
            `${base}/uploads/${encodeURIComponent(sessionId)}/complete`,
            {},
            { headers: json, signal }
          ),
        signal
      ),
    cancel: (sessionId: string) =>
      requestIdleWorkflowFileSession(() =>
        rest.delete<WorkflowFileUploadProgress>(
          `${base}/uploads/${encodeURIComponent(sessionId)}`
        )
      ),
    remove: (ids: string[], signal?: AbortSignal) =>
      requestWorkflowFile(
        () =>
          rest.post<{ ids: string[] }, { data: WorkflowFileRemoveResult }>(
            base + '/remove',
            { ids },
            { headers: json, signal }
          ),
        signal
      ),
  }
}

/**
 * Extracts a displayable message from a file request failure.
 *
 * `denied` reports authorization loss so callers can disable further file
 * operations.
 */
export function workflowFileRequestError(error: unknown) {
  if (axios.isCancel(error))
    return {
      message: 'The file operation was interrupted. Please try again.',
      denied: false,
    }
  let message = 'The file operation failed. Refresh and try again.'
  let denied = false
  if (axios.isAxiosError(error)) {
    denied = error.response?.status === 401
    const data: unknown = error.response?.data
    if (
      data &&
      typeof data === 'object' &&
      'message' in data &&
      typeof data.message === 'string'
    )
      message = data.message
  } else if (error instanceof Error) message = error.message
  return { message, denied }
}
